/**
 * Batched small creatures: ONE InstancedMesh per (geometry, material) for every copy in the
 * level (WebGPURenderer has real per-object CPU cost). Stage logic keeps working with plain
 * Object3D "proxies" (position/rotation/scale/visible, render-interpolated via ctx.smooth);
 * call sync() from frame() to write the proxies into the instance buffers.
 */
import {
  BufferGeometry,
  Color,
  DoubleSide,
  Euler,
  InstancedMesh,
  Matrix4,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  Quaternion,
  SphereGeometry,
  Vector3,
  type ColorRepresentation,
  type Material,
} from 'three';
import { mergeGeometries } from './plants';
import { tubeGeometry } from './geometry';

const ZERO = new Matrix4().makeScale(0, 0, 0);
const c = new Color();

export class InstancedProxies {
  readonly mesh: InstancedMesh;
  readonly proxies: Object3D[] = [];

  constructor(geometry: BufferGeometry, material: Material, max: number) {
    this.mesh = new InstancedMesh(geometry, material, max);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false; // instances roam the whole level
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = true;
  }

  /** New proxy (not rendered itself); `color` tints this instance. */
  add(color: ColorRepresentation = 0xffffff): Object3D {
    const o = new Object3D();
    const i = this.proxies.length;
    this.proxies.push(o);
    this.mesh.setColorAt(i, c.set(color));
    this.mesh.setMatrixAt(i, ZERO);
    this.mesh.count = this.proxies.length;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    return o;
  }

  sync(): void {
    const ps = this.proxies;
    for (let i = 0; i < ps.length; i++) {
      const o = ps[i];
      if (!o.visible) {
        this.mesh.setMatrixAt(i, ZERO);
        continue;
      }
      o.updateMatrix();
      this.mesh.setMatrixAt(i, o.matrix);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}

const wq = new Quaternion();
const we = new Euler();
const wp = new Vector3();
const ws = new Vector3();
const wl = new Matrix4();
const wm = new Matrix4();

/** Meganeura-style dragonflies: bodies, eyes and flapping wings as three InstancedMeshes. */
export class DragonflySwarm {
  readonly bodies: InstancedProxies;
  readonly eyes: InstancedMesh;
  readonly wings: InstancedMesh;
  private readonly size: number;

  constructor(max: number, size = 0.5, wingColor = '#cfe8ff') {
    this.size = size;
    const bodyGeo = tubeGeometry({
      points: [new Vector3(0, 0, size * 0.55), new Vector3(0, 0, size * 0.3), new Vector3(0, 0, -size * 0.1), new Vector3(0, 0, -size * 0.75)],
      radius: [size * 0.12, size * 0.13, size * 0.06, size * 0.03],
      segments: 12,
      radial: 8,
      top: new Color('#ffffff'),
      belly: new Color('#c8d0c8'),
    });
    const bodyMat = new MeshStandardMaterial({ vertexColors: true, roughness: 0.4, metalness: 0.25 });
    this.bodies = new InstancedProxies(bodyGeo, bodyMat, max);
    const eyeParts = [-1, 1].map((side) => new SphereGeometry(size * 0.09, 8, 6).translate(side * size * 0.08, size * 0.05, size * 0.5));
    this.eyes = new InstancedMesh(mergeGeometries(eyeParts)!, new MeshStandardMaterial({ color: '#2b7a3a', roughness: 0.2, emissive: '#1a4a20', emissiveIntensity: 0.6 }), max);
    const wg = new PlaneGeometry(size * 0.9, size * 0.18);
    wg.translate(size * 0.45, 0, 0);
    this.wings = new InstancedMesh(wg, new MeshStandardMaterial({ color: wingColor, transparent: true, opacity: 0.45, side: DoubleSide, roughness: 0.2, metalness: 0.3, depthWrite: false }), max * 4);
    for (const m of [this.eyes, this.wings]) {
      m.count = 0;
      m.frustumCulled = false;
    }
  }

  get objects(): Object3D[] {
    return [this.bodies.mesh, this.eyes, this.wings];
  }

  add(color: ColorRepresentation): Object3D {
    const o = this.bodies.add(color);
    this.eyes.count = this.bodies.proxies.length;
    this.wings.count = this.bodies.proxies.length * 4;
    return o;
  }

  /** Write all instances (call from frame, after positions are interpolated). */
  sync(time: number): void {
    this.bodies.sync();
    const ps = this.bodies.proxies;
    const s = this.size;
    for (let i = 0; i < ps.length; i++) {
      const o = ps[i];
      if (!o.visible) {
        this.eyes.setMatrixAt(i, ZERO);
        for (let k = 0; k < 4; k++) this.wings.setMatrixAt(i * 4 + k, ZERO);
        continue;
      }
      this.eyes.setMatrixAt(i, o.matrix);
      const a = Math.sin(time * 42 + i * 1.7) * 0.55;
      for (let k = 0; k < 4; k++) {
        const side = k % 2 === 0 ? -1 : 1;
        we.set(-Math.PI / 2, side * a * (k < 2 ? 1 : -0.8), 0);
        wq.setFromEuler(we);
        wp.set(0, s * 0.1, k < 2 ? s * 0.15 : -s * 0.05);
        ws.set(side, 1, 1);
        wl.compose(wp, wq, ws);
        wm.multiplyMatrices(o.matrix, wl);
        this.wings.setMatrixAt(i * 4 + k, wm);
      }
    }
    this.eyes.instanceMatrix.needsUpdate = true;
    this.wings.instanceMatrix.needsUpdate = true;
  }
}
